import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { CartItem } from "@/types";
import { clearAuth } from "./auth.slice";

interface CartState {
  items: CartItem[];
  total: number;
  serverCount: number | null;
  syncedLocalCount: number;
}

const initialState: CartState = {
  items: [],
  total: 0,
  serverCount: null,
  syncedLocalCount: 0,
};

const calculateTotal = (items: CartItem[]) =>
  items.reduce((sum, item) => sum + item.price * item.quantity, 0);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<Omit<CartItem, "quantity">>) => {
      const existingItem = state.items.find(
        (item) => item.productId === action.payload.productId,
      );

      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        state.items.push({ ...action.payload, quantity: 1 });
      }

      state.total = calculateTotal(state.items);
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(
        (item) => item.productId !== action.payload,
      );
      state.total = calculateTotal(state.items);
    },
    updateQuantity: (
      state,
      action: PayloadAction<{ productId: string; quantity: number }>,
    ) => {
      const item = state.items.find(
        (cartItem) => cartItem.productId === action.payload.productId,
      );

      if (!item) {
        return;
      }

      item.quantity = action.payload.quantity;
      state.items = state.items.filter((cartItem) => cartItem.quantity > 0);
      state.total = calculateTotal(state.items);
    },
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
    },
    setServerCount: (
      state,
      action: PayloadAction<{ count: number; syncedLocalCount: number }>,
    ) => {
      state.serverCount = action.payload.count;
      state.syncedLocalCount = action.payload.syncedLocalCount;
    },
    clearServerCount: (state) => {
      state.serverCount = null;
      state.syncedLocalCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearAuth, (state) => {
      state.serverCount = null;
      state.syncedLocalCount = 0;
    });
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  setServerCount,
  clearServerCount,
} = cartSlice.actions;

export default cartSlice.reducer;
